/**
 * Shared Web Audio helpers for lead-pop-game SFX.
 * Muted by default; persisted in localStorage.
 */

const STORAGE_KEY = 'leadPop.sound';

/** @type {AudioContext | null} */
let ctx = null;
let muted = readMuted();

function readMuted() {
    try {
        if (typeof localStorage === 'undefined') return true;
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw === null ? true : raw !== '1';
    } catch {
        return true;
    }
}

function persistMuted() {
    try {
        if (typeof localStorage === 'undefined') return;
        localStorage.setItem(STORAGE_KEY, muted ? '0' : '1');
    } catch {
        // ignore
    }
}

export function isMuted() {
    return muted;
}

/** @param {boolean} value */
export function setMuted(value) {
    muted = !!value;
    persistMuted();
}

export function toggleMuted() {
    setMuted(!muted);
    return muted;
}

function ensureCtx() {
    if (ctx) return ctx;
    if (typeof window === 'undefined') return null;
    const Ctor = window.AudioContext || /** @type {any} */ (window).webkitAudioContext;
    if (!Ctor) return null;
    try {
        ctx = new Ctor();
    } catch {
        ctx = null;
    }
    return ctx;
}

/** Resume AudioContext after a user gesture (browser autoplay policy). */
export function unlockAudio() {
    const audio = ensureCtx();
    if (!audio) return;
    if (audio.state === 'suspended') {
        audio.resume().catch(() => {});
    }
}

/** Shared AudioContext for SFX and procedural music. */
export function getAudioContext() {
    return ensureCtx();
}

/**
 * @param {Object} params
 * @param {number} params.startHz
 * @param {number} params.endHz
 * @param {number} params.durationMs
 * @param {number} [params.delayMs]
 * @param {OscillatorType} [params.type]
 * @param {number} [params.gain]
 */
export function tone({ startHz, endHz, durationMs, delayMs = 0, type = 'square', gain = 0.08 }) {
    if (muted) return;
    const audio = ensureCtx();
    if (!audio) return;

    const now = audio.currentTime + delayMs / 1000;
    const end = now + durationMs / 1000;
    const osc = audio.createOscillator();
    const g = audio.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(startHz, now);
    osc.frequency.linearRampToValueAtTime(endHz, end);
    g.gain.setValueAtTime(0, now);
    g.gain.linearRampToValueAtTime(gain, now + 0.01);
    g.gain.linearRampToValueAtTime(0, end);
    osc.connect(g).connect(audio.destination);
    osc.start(now);
    osc.stop(end + 0.02);
}

/**
 * @param {Array<{ startHz: number, endHz: number, durationMs: number, delayMs?: number, type?: OscillatorType, gain?: number }>} steps
 */
export function toneSequence(steps) {
    steps.forEach((step) => tone(step));
}

/**
 * Short bubble-pop: fast attack + exponential decay + downward pitch sweep.
 * @param {Object} params
 * @param {number} [params.startHz]
 * @param {number} [params.endHz]
 * @param {number} [params.durationMs]
 * @param {number} [params.delayMs]
 * @param {OscillatorType} [params.type]
 * @param {number} [params.gain]
 * @param {number} [params.attackMs]
 */
export function popTone({
    startHz = 720,
    endHz = 150,
    durationMs = 52,
    delayMs = 0,
    type = 'sine',
    gain = 0.1,
    attackMs = 2,
} = {}) {
    if (muted) return;
    const audio = ensureCtx();
    if (!audio) return;

    const now = audio.currentTime + delayMs / 1000;
    const end = now + durationMs / 1000;
    const attackSec = Math.min(attackMs / 1000, (durationMs / 1000) * 0.4);
    const peakGain = Math.max(0.0001, gain);

    const osc = audio.createOscillator();
    const g = audio.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(Math.max(40, startHz), now);
    osc.frequency.exponentialRampToValueAtTime(Math.max(40, endHz), end);

    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(peakGain, now + attackSec);
    g.gain.exponentialRampToValueAtTime(0.0001, end);

    osc.connect(g).connect(audio.destination);
    osc.start(now);
    osc.stop(end + 0.02);
}

/**
 * Arcade card pop — square body, rising chip blip, short high sparkle.
 * @param {{ rootHz?: number, delayMs?: number, gain?: number }} [params]
 */
export function popBurst({ rootHz = 720, delayMs = 0, gain = 0.1 } = {}) {
    popTone({
        startHz: rootHz * 2.6,
        endHz: rootHz * 0.9,
        durationMs: 34,
        delayMs,
        gain: gain * 0.92,
        type: 'square',
        attackMs: 1,
    });
    popTone({
        startHz: rootHz * 1.35,
        endHz: rootHz * 3.4,
        durationMs: 20,
        delayMs: delayMs + 1,
        gain: gain * 0.58,
        type: 'square',
        attackMs: 0.5,
    });
    popTone({
        startHz: rootHz * 4.8,
        endHz: rootHz * 2.6,
        durationMs: 14,
        delayMs: delayMs + 3,
        gain: gain * 0.32,
        type: 'triangle',
        attackMs: 0.5,
    });
}

/**
 * Arcade “wrong hit” — downward square/saw (inverse of {@link popBurst}).
 * @param {{ rootHz?: number, delayMs?: number, gain?: number }} [params]
 */
export function badBurst({ rootHz = 440, delayMs = 0, gain = 0.11 } = {}) {
    popTone({
        startHz: rootHz * 1.15,
        endHz: rootHz * 0.32,
        durationMs: 50,
        delayMs,
        gain: gain * 0.92,
        type: 'square',
        attackMs: 1,
    });
    popTone({
        startHz: rootHz * 0.95,
        endHz: rootHz * 0.25,
        durationMs: 68,
        delayMs: delayMs + 8,
        gain: gain * 0.78,
        type: 'sawtooth',
        attackMs: 1,
    });
    popTone({
        startHz: rootHz * 1.5,
        endHz: rootHz * 0.55,
        durationMs: 32,
        delayMs: delayMs + 3,
        gain: gain * 0.42,
        type: 'square',
        attackMs: 0.5,
    });
}
